/**
 * Étape 1 — Votre enfant.
 *
 * Deux raccourcis y font l'essentiel du gain de temps, et ils se méritent :
 * reprendre l'adresse du parent, et recopier une fiche déjà remplie pour un
 * frère ou une sœur. Une famille de trois enfants saisissait trois fois la même
 * adresse, la même école et le même médecin.
 *
 * La photo ouvre l'étape : c'est le geste qu'on attend en premier dans un
 * formulaire de ce genre, et le seul qui demande de quitter le clavier. La
 * reléguer en bas la faisait manquer à qui remplissait au fil du texte.
 *
 * Elle est choisie ici mais téléversée à la fin, une fois l'enfant créé : elle
 * a besoin de son identifiant pour avoir un nom de fichier.
 *
 * ## Une photo déjà enregistrée s'affiche
 *
 * `photoExistante` porte l'URL signée de celle qui est déjà en banque. Sans
 * elle, la vérification d'une fiche ouvrait sur une pastille grise et le bouton
 * « Ajouter une photo » : le parent croyait sa photo perdue et la téléversait à
 * nouveau, alors qu'elle n'avait jamais bougé.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Copy, Loader2, Trash2, Upload, User } from 'lucide-react';
import clsx from 'clsx';
import { useSchoolStore } from '../../../stores/schoolStore';
import { useAuthStore } from '../../../stores/authStore';
import BelgianNRNInput from '../../common/BelgianNRNInput';
import { optimizeImageIfNeeded } from '../../../utils/imageUtils';
import { Bloc, Case, Liste, Navigation, Pourquoi, Saisie } from './champs';
import { adresseDuProfil, nrnRetenu, prefixeNRN, type Erreurs, type Identite } from './fiche';
import {
  CLASSE_HORS_LISTE,
  classeAChoisir,
  classesDeLEcole,
  ecolesSemblables,
  optionsDeClasse,
  type ClassesConnues,
} from '../../../lib/classes/regles';
import { libelleAnneeScolaire } from '../../../lib/jpa/regles';

/** Un enfant déjà encodé, dont on peut reprendre les informations communes. */
export interface FratrieProposee {
  id: string;
  prenom: string;
}

const EtapeIdentite = ({
  valeur,
  onChange,
  erreurs,
  onSuivant,
  photo,
  onPhoto,
  photoExistante,
  fratrie,
  onCopierFratrie,
  classes,
  annee,
  onSignalerClasse,
}: {
  valeur: Identite;
  onChange: (v: Identite) => void;
  erreurs: Erreurs;
  onSuivant: () => void;
  photo: File | null;
  onPhoto: (f: File | null) => void;
  /** L'URL signée de la photo déjà en banque, s'il y en a une. */
  photoExistante?: string | null;
  fratrie: FratrieProposee[];
  onCopierFratrie: (id: string) => void;
  /** Les centres reliés aux écoles et leurs classes ; sans eux, la classe n'est pas demandée. */
  classes?: ClassesConnues;
  /** L'année scolaire en cours (2026 pour 2026-2027). */
  annee?: number;
  /** Prévient l'équipe qu'une classe manque à la liste de l'école. */
  onSignalerClasse?: (classe: string) => Promise<void>;
}) => {
  const { schools, isLoading: chargementEcoles, fetchSchools } = useSchoolStore();
  const { profile } = useAuthStore();
  const [apercu, setApercu] = useState<string | null>(null);
  const [erreurPhoto, setErreurPhoto] = useState<string | null>(null);
  /* « Ma classe n'est pas dans la liste » : le nom que le parent lui donne, et l'envoi. */
  const [horsListeOuvert, setHorsListeOuvert] = useState(false);
  const [classeNommee, setClasseNommee] = useState('');
  const [signalement, setSignalement] = useState<'repos' | 'envoi' | 'echec'>('repos');

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  /* L'aperçu suit le fichier choisi, et l'URL est libérée avec lui. */
  useEffect(() => {
    if (!photo) {
      setApercu(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const maj = (champs: Partial<Identite>) => onChange({ ...valeur, ...champs });

  /* Le fichier qu'on vient de choisir l'emporte ; sinon celui deja en banque. */
  const affichee = apercu || photoExistante || null;

  /*
    L'adresse du parent, déjà donnée dans « Vos coordonnées ». Cochée, la case
    reprend cette adresse et masque les trois champs : le parent ne la retape
    pas pour chaque enfant. Décochée, les champs se vident pour une autre
    adresse (des parents séparés, une garde alternée).
  */
  const adresseParent = adresseDuProfil(profile);
  const reprendAdresse = !!adresseParent && valeur.memeAdresseQueParent;
  // Une adresse de profil que la fiche refuse (code postal étranger…) se montre pour être corrigée
  const erreurAdresse = !!(erreurs.adresse || erreurs.cPostal || erreurs.localite);
  const champsAdresseVisibles = !reprendAdresse || erreurAdresse;

  const basculerAdresse = (cochee: boolean) =>
    maj(
      cochee && adresseParent
        ? { ...adresseParent, memeAdresseQueParent: true }
        : { adresse: '', cPostal: '', localite: '', memeAdresseQueParent: false },
    );

  /*
    La classe, quand l'école choisie est reliée à un centre qui en a. Elle
    vaut pour une année scolaire ; les classes fermées ne sont proposées que
    si c'est déjà celle de l'enfant.
  */
  const classesEcole = classes ? classesDeLEcole(valeur.ecole, classes) : [];
  const demanderClasse = !!classes && classeAChoisir(valeur.ecole, classes);
  const classeSignalee = valeur.classeId === CLASSE_HORS_LISTE;

  const changerEcole = (ecole: string) => {
    /* Changer d'école : la classe de l'année ne vaut plus. */
    maj({ ecole, classeId: '' });
    setHorsListeOuvert(false);
  };

  const signaler = async () => {
    if (!onSignalerClasse || !classeNommee.trim()) return;
    setSignalement('envoi');
    try {
      await onSignalerClasse(classeNommee.trim());
      maj({ classeId: CLASSE_HORS_LISTE });
      setHorsListeOuvert(false);
      setSignalement('repos');
    } catch {
      setSignalement('echec');
    }
  };

  /* Une école tapée à la main : celles de la liste qui lui ressemblent. */
  const semblables = valeur.ecole === 'other' ? ecolesSemblables(valeur.ecoleAutre, schools) : [];

  const choisirPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files?.[0];
    /* Le champ est réinitialisé : sans cela, rechoisir le même fichier ne déclenche rien. */
    e.target.value = '';
    setErreurPhoto(null);
    if (!fichier) return;

    if (!['image/jpeg', 'image/png'].includes(fichier.type)) {
      setErreurPhoto('Seuls les formats JPG et PNG sont acceptés.');
      return;
    }
    if (fichier.size > 5 * 1024 * 1024) {
      setErreurPhoto('La photo ne doit pas dépasser 5 Mo.');
      return;
    }

    try {
      onPhoto(await optimizeImageIfNeeded(fichier));
    } catch {
      setErreurPhoto("La photo n'a pas pu être préparée. Réessayez avec une autre image.");
    }
  };

  return (
    <div className="space-y-8">
      {fratrie.length > 0 && (
        <div className="rounded-xl border border-primary-100 bg-primary-50 p-4">
          <p className="text-sm font-medium text-gray-900">
            Vous avez déjà une fiche complétée
          </p>
          <p className="mt-0.5 text-sm text-gray-600">
            Reprenez l’adresse, l’école, le médecin et les personnes autorisées d’un autre de
            vos enfants&nbsp;; vous
            pourrez tout modifier ensuite.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {fratrie.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onCopierFratrie(f.id)}
                className="inline-flex items-center rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                Copier depuis {f.prenom}
              </button>
            ))}
          </div>
        </div>
      )}

      <Bloc
        titre="Sa photo"
        intro="Elle aide l’équipe à reconnaître votre enfant le premier jour. Elle reste dans l’application et n’est jamais publiée."
      >
        <div className="flex items-center gap-5">
          <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-full bg-gray-100">
            {affichee ? (
              <img
                src={affichee}
                alt={photo ? 'Aperçu de la nouvelle photo' : 'Photo enregistrée'}
                className="h-full w-full object-cover"
              />
            ) : (
              <User className="h-full w-full p-6 text-gray-300" />
            )}
          </div>
          <div className="space-y-2">
            <label className="btn-outline inline-flex cursor-pointer items-center">
              <Upload className="mr-2 h-4 w-4" />
              {affichee ? 'Changer la photo' : 'Ajouter une photo'}
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={choisirPhoto}
                className="hidden"
              />
            </label>
            {photo && (
              <button
                type="button"
                onClick={() => onPhoto(null)}
                className="ml-2 inline-flex items-center text-sm text-gray-500 hover:text-red-600"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                {/*
                  Avec une photo deja en banque, ce bouton ne supprime rien : il
                  abandonne le fichier qu'on vient de choisir et rend la
                  precedente. Le dire, sinon « Retirer » laisse croire qu'on
                  efface la photo de l'enfant.
                */}
                {photoExistante ? 'Annuler ce changement' : 'Retirer'}
              </button>
            )}
            {!photo && photoExistante && (
              <p className="text-xs text-gray-500">Photo déjà enregistrée&nbsp;— rien à refaire.</p>
            )}
            <p className="text-xs text-gray-500">JPG ou PNG, 5 Mo maximum. Facultatif.</p>
            {erreurPhoto && <p className="text-sm text-red-600">{erreurPhoto}</p>}
          </div>
        </div>
      </Bloc>

      <Bloc titre="Son identité">
        <div className="grid gap-5 md:grid-cols-2">
          <Saisie
            label="Prénom"
            requis
            valeur={valeur.prenom}
            onChange={(v) => maj({ prenom: v })}
            erreur={erreurs.prenom}
            autoComplete="off"
          />
          <Saisie
            label="Nom"
            requis
            valeur={valeur.nom}
            onChange={(v) => maj({ nom: v })}
            erreur={erreurs.nom}
            autoComplete="off"
          />
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <Saisie
            label="Date de naissance"
            requis
            type="date"
            valeur={valeur.dateNaissance}
            onChange={(v) => maj({ dateNaissance: v })}
            erreur={erreurs.dateNaissance}
          />

          <div>
            <label className="mb-1 flex flex-wrap items-center gap-2 text-sm font-medium text-gray-700">
              Numéro de registre national
              {/*
                Un signal orange, jamais un blocage.

                876 enfants n'en ont pas, et l'exiger fermerait la porte a une
                famille sur trois. Mais se taire le ferait decouvrir en janvier,
                quand l'attestation fiscale n'arrive pas : la pastille dit qu'il
                manque quelque chose, la phrase dessous dit quoi, et le parent
                passe a la suite s'il ne l'a pas sous la main.
              */}
              {!nrnRetenu(valeur) ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-200">
                  <AlertCircle className="h-3 w-3" aria-hidden="true" />
                  À compléter
                </span>
              ) : (
                <span className="text-xs font-normal text-gray-400">facultatif</span>
              )}
            </label>
            <BelgianNRNInput
              value={valeur.nNational}
              onChange={(v) => maj({ nNational: v })}
              error={nrnRetenu(valeur) ? erreurs.nNational : undefined}
              prefixe={prefixeNRN(valeur.dateNaissance)}
            />
            {/*
              Le champ est facultatif : un numéro faux ne doit pas bloquer
              l'inscription. On propose de le retirer plutôt que de laisser le
              parent chercher une erreur qu'il ne peut pas corriger sans la carte.
            */}
            {erreurs.nNational && nrnRetenu(valeur) && (
              <button
                type="button"
                onClick={() => maj({ nNational: '' })}
                className="mt-1 text-sm font-medium text-primary-600 underline hover:text-primary-700"
              >
                Continuer sans ce numéro (vous pourrez l’ajouter plus tard)
              </button>
            )}
            {!(erreurs.nNational && nrnRetenu(valeur)) && (
              <p
                className={clsx(
                  'mt-1 text-xs',
                  nrnRetenu(valeur) ? 'text-gray-500' : 'text-amber-700',
                )}
              >
                Sans lui, nous ne pouvons pas éditer l’attestation fiscale. Il n’empêche pas
                d’inscrire votre enfant&nbsp;: vous pourrez le renseigner plus tard.
              </p>
            )}
            <Pourquoi>
              Il ne sert qu’aux attestations fiscales et aux obligations qui y sont directement
              liées&nbsp;: déclarations à l’ONE et aux communes partenaires. Il n’apparaît sur
              aucune liste de présence.
            </Pourquoi>
          </div>
        </div>
      </Bloc>

      <Bloc
        titre="Son adresse"
        intro="Celle de l’enfant, qui peut différer de la vôtre — c’est elle qui détermine le tarif local."
      >
        {adresseParent && (
          <Case
            coche={valeur.memeAdresseQueParent}
            onChange={basculerAdresse}
            label={profile?.account_type === 'organisation' ? 'Même adresse que l’organisation' : 'Même adresse que moi'}
            aide={`${adresseParent.adresse}, ${adresseParent.cPostal} ${adresseParent.localite}`}
          />
        )}

        {champsAdresseVisibles && (
          <>
            <Saisie
              label="Adresse"
              requis
              valeur={valeur.adresse}
              onChange={(v) => maj({ adresse: v })}
              erreur={erreurs.adresse}
              placeholder="Rue et numéro"
            />

            <div className="grid gap-5 md:grid-cols-2">
              <Saisie
                label="Code postal"
                requis
                valeur={valeur.cPostal}
                onChange={(v) => maj({ cPostal: v.replace(/\D/g, '').slice(0, 4) })}
                erreur={erreurs.cPostal}
                placeholder="1030"
              />
              <Saisie
                label="Localité"
                requis
                valeur={valeur.localite}
                onChange={(v) => maj({ localite: v })}
                erreur={erreurs.localite}
                placeholder="Schaerbeek"
              />
            </div>
          </>
        )}

        <Liste
          label="École"
          requis
          valeur={valeur.ecole}
          onChange={changerEcole}
          erreur={erreurs.ecole}
          vide={chargementEcoles ? 'Chargement des écoles…' : 'Sélectionner une école'}
          options={[
            ...schools.map((e) => ({
              valeur: e.id,
              libelle: `${e.name} (${e.code_postal})`,
            })),
            { valeur: 'other', libelle: 'Autre / école non listée' },
          ]}
          pourquoi="L’école détermine le tarif local et nous permet d’organiser les ramassages quand ils existent."
        />

        {valeur.ecole === 'other' && (
          <>
            <Saisie
              label="Nom de l’école"
              requis
              valeur={valeur.ecoleAutre}
              onChange={(v) => maj({ ecoleAutre: v })}
              erreur={erreurs.ecoleAutre}
              placeholder="Nom complet de l’établissement"
              aide="Sans une école de la liste, votre enfant n’a ni tarif local ni accès aux journées pédagogiques de son école."
            />
            {semblables.length > 0 && (
              <div className="rounded-xl border border-primary-100 bg-primary-50 p-4">
                <p className="text-sm font-medium text-gray-900">Est-ce l’une de ces écoles&nbsp;?</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {semblables.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => maj({ ecole: e.id, ecoleAutre: '', classeId: '' })}
                      className="inline-flex items-center rounded-lg border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
                    >
                      {e.name} ({e.code_postal})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {demanderClasse && annee !== undefined && (
          <div className="space-y-3">
            {classeSignalee ? (
              <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                <p className="inline-flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  Classe signalée à l’équipe
                </p>
                <p className="mt-1">
                  Nous l’ajouterons à la liste&nbsp;; vous pourrez la choisir plus tard depuis la fiche de
                  votre enfant.{' '}
                  <button
                    type="button"
                    onClick={() => maj({ classeId: '' })}
                    className="font-medium underline hover:text-green-700"
                  >
                    Choisir dans la liste
                  </button>
                </p>
              </div>
            ) : (
              <>
                <Liste
                  label={`Sa classe en ${libelleAnneeScolaire(annee)}`}
                  requis
                  valeur={valeur.classeId}
                  onChange={(v) => maj({ classeId: v })}
                  erreur={erreurs.classeId}
                  vide="Choisir sa classe…"
                  options={optionsDeClasse(classesEcole, valeur.classeId)}
                  pourquoi="Les journées pédagogiques et les activités parascolaires de l’école sont ouvertes classe par classe. La classe se redemande à chaque rentrée."
                />
                {onSignalerClasse && !horsListeOuvert && (
                  <button
                    type="button"
                    onClick={() => setHorsListeOuvert(true)}
                    className="text-sm font-medium text-primary-600 underline hover:text-primary-700"
                  >
                    Sa classe n’est pas dans la liste
                  </button>
                )}
                {onSignalerClasse && horsListeOuvert && (
                  <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <Saisie
                      label="Sa classe, telle que l’école la nomme"
                      valeur={classeNommee}
                      onChange={setClasseNommee}
                      placeholder="P3B — Madame Inès"
                      aide="Nous prévenons l’équipe, qui l’ajoutera à la liste. Vous pourrez continuer sans attendre."
                    />
                    {signalement === 'echec' && (
                      <p className="text-sm text-red-600">Le signalement n’a pas pu partir. Réessayez dans un instant.</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void signaler()}
                        disabled={signalement === 'envoi' || !classeNommee.trim()}
                        className="btn-primary inline-flex items-center disabled:opacity-60"
                      >
                        {signalement === 'envoi' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Prévenir l’équipe
                      </button>
                      <button type="button" onClick={() => setHorsListeOuvert(false)} className="btn-outline">
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Bloc>

      <Navigation onSuivant={onSuivant} />
    </div>
  );
};

export default EtapeIdentite;
